import React, { useCallback, useEffect, useMemo, useState } from "react";
import { exportRushReport } from "../../actions/studensts";
import AlertDenger from "../../components/AlertDenger";
import TablePagination from "../../components/TablePagination";
import RushReminderModal from "./RushReminderModal";

const PAGE_SIZE = 100;

// Hidden for now; set to true to show the Actual Authentication Time column.
const SHOW_ACTUAL_AUTH_TIME = false;

// Mirrors RUSH_REMINDER_MANAGERS / RUSH_REMINDER_MANAGER_ROLES in
// Server/src/components/rush_reminder/rush_reminder.routes.js.
const RUSH_REMINDER_MANAGERS = ["admin"];
const RUSH_REMINDER_MANAGER_ROLES = ["super-admin"];

const defaultDateFrom = () => "2026-01-01";
const defaultDateTo = () => new Date().toISOString().slice(0, 10);

const formatDate = (value) => (value ? String(value).slice(0, 10) : "");

// A rush student counts as delivered once their case reaches one of these
// states; anything else is still in progress.
const DELIVERED_STATES = ["mohe_validation", "mohe", "done"];

const STATE_LABELS = {
  auditing_new: "Auditing New",
  request_edit: "Request Edit",
  invoice: "Invoice",
  invoice_wait: "Invoice Wait",
  another_invoice: "Another Invoice",
  score_validation: "Score Validation",
  score_request: "Score Request",
  score_pending: "Score Pending",
  print: "Print",
  print_validation: "Print Validation",
  moe_validation: "MOE Validation",
  moe: "MOE",
  moe_pending: "MOE Pending",
  moe_hold: "MOE Hold",
  aatc_review: "AATC Review",
  deliver: "Deliver",
  mohe_validation: "MOHE Validation",
  mohe: "MOHE",
  done: "Done",
};
const stateLabel = (s) => STATE_LABELS[s] || s || "";

const isDelivered = (state) => DELIVERED_STATES.includes(state);

// Whole days between two dates (UTC-based so it doesn't shift with the
// viewer's time of day). Either end defaults to today when omitted.
const daysBetween = (fromStr, toStr) => {
  if (!fromStr) return null;
  const start = new Date(`${formatDate(fromStr)}T00:00:00Z`);
  if (isNaN(start)) return null;
  let end;
  if (toStr) {
    end = new Date(`${formatDate(toStr)}T00:00:00Z`);
    if (isNaN(end)) return null;
  } else {
    const today = new Date();
    end = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));
  }
  return Math.max(0, Math.round((end - start) / 86400000));
};

// Whole days between the Authentication Date and today.
const daysSince = (dateStr) => daysBetween(dateStr, null);

const formatDays = (n) => (n === null || n === undefined ? "—" : `${n} day${n === 1 ? "" : "s"}`);

function DeliveryBadge({ state }) {
  const delivered = isDelivered(state);
  return (
    <span
      className={`inline-block whitespace-nowrap rounded-md border text-xs font-semibold px-2 py-0.5 ${
        delivered
          ? "bg-green-100 text-green-800 border-green-300"
          : "bg-amber-100 text-amber-800 border-amber-300"
      }`}
    >
      {delivered ? "Delivered" : "Not Delivered"}
    </span>
  );
}

export default function RushStudents() {
  const [page, setPage] = useState(1);
  const [dateFrom, setDateFrom] = useState(defaultDateFrom());
  const [dateTo, setDateTo] = useState(defaultDateTo());
  const [search, setSearch] = useState("");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [showReminders, setShowReminders] = useState(false);

  const profile = JSON.parse(localStorage.getItem("profile"))?.value?.[0];
  const canManageReminders =
    RUSH_REMINDER_MANAGERS.includes(profile?.username) ||
    RUSH_REMINDER_MANAGER_ROLES.includes(profile?.role);

  const flashError = (msg) => {
    setErrorMsg(msg);
    setTimeout(() => setErrorMsg(""), 3500);
  };

  const fetchRows = useCallback(async () => {
    if (!dateFrom || !dateTo) {
      flashError("From and To dates are required.");
      return;
    }
    setLoading(true);
    const res = await exportRushReport(dateFrom, dateTo);
    setLoading(false);

    if (res && res.status === 200) {
      setRows(res.data?.data || []);
      setPage(1);
    } else {
      flashError(res?.data?.ERROR || "Failed to load Rush Students.");
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    fetchRows();
    // Only run once on mount with the default (all-time) range; further
    // loads are triggered explicitly via the Apply Filters button.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter(
      (r) =>
        String(r.national_id || "").toLowerCase().includes(term) ||
        String(r.full_name || "").toLowerCase().includes(term) ||
        String(r.phone || "").toLowerCase().includes(term)
    );
  }, [rows, search]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const pagedRows = useMemo(
    () => filteredRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filteredRows, page]
  );

  const deliveredCount = useMemo(
    () => filteredRows.filter((r) => isDelivered(r.state)).length,
    [filteredRows]
  );

  const inputClass =
    "border border-gray-300 rounded-lg p-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-400";

  return (
    <div className="px-4">
      {errorMsg && <AlertDenger text={errorMsg} />}

      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Rush Students</h1>
          <p className="text-xs text-gray-500 mt-1">
            Students who paid the rush fee, sorted by Authentication Date (the
            newer of the Invoice Date and the Another Invoice Date). A
            student is Delivered once their state is MOHE Validation, MOHE, or
            Done — any other state is Not Delivered.
          </p>
        </div>
        {canManageReminders && (
          <button
            type="button"
            onClick={() => setShowReminders(true)}
            className="px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700"
          >
            Reminder Emails
          </button>
        )}
      </div>

      {showReminders && <RushReminderModal onClose={() => setShowReminders(false)} />}

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4 bg-white border rounded-lg shadow p-4">
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">
            From (Authentication Date)
          </label>
          <input
            type="date"
            className={inputClass}
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">
            To (Authentication Date)
          </label>
          <input
            type="date"
            className={inputClass}
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </div>
        <div className="flex items-end">
          <button
            type="button"
            onClick={fetchRows}
            disabled={loading}
            className="px-4 py-2 bg-blue-800 text-white rounded-lg text-sm font-medium
                       hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed w-full"
          >
            {loading ? "Loading..." : "Apply Filters"}
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-3">
        <input
          type="text"
          placeholder="Search by National ID, name, or phone"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="border border-gray-300 rounded-lg p-2 text-sm w-64
                     focus:outline-none focus:ring-2 focus:ring-blue-400"
        />
        <span className="px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-700 font-semibold">
          {filteredRows.length} Rush Students
        </span>
        <span className="px-3 py-1 rounded-full text-sm bg-green-100 text-green-700 font-semibold">
          {deliveredCount} Delivered
        </span>
        <span className="px-3 py-1 rounded-full text-sm bg-amber-100 text-amber-700 font-semibold">
          {filteredRows.length - deliveredCount} Not Delivered
        </span>
      </div>

      {loading ? (
        <p className="p-4 text-gray-500">Loading...</p>
      ) : filteredRows.length === 0 ? (
        <h1
          className="w-full my-2 border py-2.5 px-5 rounded-lg bg-gray-50
                     flex items-center justify-between text-sm font-medium text-red-600"
        >
          {search.trim()
            ? "No Rush Students match your search."
            : "No Rush Students found for the selected filters."}
        </h1>
      ) : (
        <div className="overflow-x-auto border rounded-lg shadow bg-white">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-gray-100 text-left">
                <th className="px-4 py-2 border border-gray-200">National ID</th>
                <th className="px-4 py-2 border border-gray-200">Full Name</th>
                <th className="px-4 py-2 border border-gray-200">Phone Number</th>
                <th className="px-4 py-2 border border-gray-200">State</th>
                <th className="px-4 py-2 border border-gray-200">Authentication Date</th>
                <th className="px-4 py-2 border border-gray-200">Days Since Authentication</th>
                <th className="px-4 py-2 border border-gray-200">Delivered After</th>
                {SHOW_ACTUAL_AUTH_TIME && (
                  <th
                    className="px-4 py-2 border border-gray-200"
                    title="Authentication Date until Delivery Hold, or until a later earlier-stage date if the student went back"
                  >
                    Actual Authentication Time
                  </th>
                )}
                <th className="px-4 py-2 border border-gray-200">Status</th>
              </tr>
            </thead>
            <tbody>
              {pagedRows.map((row, i) => {
                const delivered = isDelivered(row.state);
                return (
                  <tr key={`${row.national_id}-${i}`} className="border-b border-gray-200">
                    <td className="px-4 py-2 border border-gray-200 whitespace-nowrap">
                      {row.national_id}
                    </td>
                    <td className="px-4 py-2 border border-gray-200">{row.full_name}</td>
                    <td className="px-4 py-2 border border-gray-200 whitespace-nowrap">
                      {row.phone}
                    </td>
                    <td className="px-4 py-2 border border-gray-200 whitespace-nowrap">
                      {stateLabel(row.state)}
                    </td>
                    <td className="px-4 py-2 border border-gray-200 whitespace-nowrap">
                      {formatDate(row.authentication_date)}
                    </td>
                    <td className="px-4 py-2 border border-gray-200 text-center">
                      {delivered ? "—" : formatDays(daysSince(row.authentication_date))}
                    </td>
                    <td className="px-4 py-2 border border-gray-200 text-center">
                      {delivered
                        ? formatDays(daysBetween(row.authentication_date, row.mohe_date))
                        : "—"}
                    </td>
                    {SHOW_ACTUAL_AUTH_TIME && (
                      <td
                        className="px-4 py-2 border border-gray-200 text-center"
                        title={row.actual_end_date ? `Until ${formatDate(row.actual_end_date)}` : undefined}
                      >
                        {formatDays(row.actual_auth_days)}
                      </td>
                    )}
                    <td className="px-4 py-2 border border-gray-200 text-center">
                      <DeliveryBadge state={row.state} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <TablePagination
        page={page}
        setPage={setPage}
        totalItems={filteredRows.length}
        pageSize={PAGE_SIZE}
      />
    </div>
  );
}
